import { useState, useEffect, useRef, useMemo } from 'react';
import { Plus, X, Trash2, Edit, Eye, Replace, Upload, BookOpen, Edit2 } from 'lucide-react';
import JoditEditor from 'jodit-react';
import axios from 'axios';
import Swal from 'sweetalert2';

const API_BASE_URL = 'https://backend.dlapakistan.com/api';
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Accept': 'application/json',
  }
});

const Home = () => {
  const [activeTab, setActiveTab] = useState('cards');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null); // Simple state for file
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [contents, setContents] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });

  const fileInputRef = useRef(null);
  const titleEditorRef = useRef(null);
  const descriptionEditorRef = useRef(null);
  const emerald600 = "#059669";

  // Strip HTML tags
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Simple editor config
  const editorConfig = useMemo(() => ({
    readonly: false,
    height: 200,
    toolbar: true,
    toolbarSticky: false,
    buttons: ['bold', 'italic', 'underline', 'ul', 'ol'],
    showCharsCounter: false,
    showWordsCounter: false,
    showPlaceholder: true,
    placeholder: 'Enter text...',
    disablePlugins: 'about,print,preview,find,image,file,video,table,link',
  }), []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let endpoint = '';

        if (activeTab === 'cards') {
          endpoint = '/cards/';
        } else if (activeTab === 'sliders') {
          endpoint = '/sliders/';
        } else if (activeTab === 'contents') {
          endpoint = '/contents/';
        }

        const response = await api.get(endpoint);
        
        if (activeTab === 'cards') {
          setCards(Array.isArray(response.data) ? response.data : []);
        } else if (activeTab === 'sliders') {
          setSliders(Array.isArray(response.data) ? response.data : []);
        } else if (activeTab === 'contents') {
          setContents(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        console.error('Fetch error:', err);
        Swal.fire('Error', 'Failed to load data', 'error');
        if (activeTab === 'cards') setCards([]);
        else if (activeTab === 'sliders') setSliders([]);
        else if (activeTab === 'contents') setContents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab]);

  // Handle file selection - SIMPLE
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('File selected:', file.name, file.type, file.size);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      Swal.fire({
        icon: "error",
        title: "Invalid File",
        text: "Please select an image file",
        confirmButtonColor: emerald600
      });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "File Too Large",
        text: "Image must be less than 5MB",
        confirmButtonColor: emerald600
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setImagePreview(event.target.result);
    };
    reader.readAsDataURL(file);
    
    // Store the file
    setSelectedFile(file);
  };

  // Handle submit - SIMPLIFIED
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get plain text
    const plainTitle = stripHtml(formData.title).trim();
    const plainDescription = stripHtml(formData.description).trim();

    // Validate
    if (!plainTitle) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill in title.",
        confirmButtonColor: emerald600
      });
      return;
    }

    if (!plainDescription) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: `Please fill in ${activeTab === 'cards' ? 'description' : 'subtitle'}.`,
        confirmButtonColor: emerald600
      });
      return;
    }

    // Check for image in create mode
    if ((activeTab === 'cards' || activeTab === 'sliders') && !isEditMode && !selectedFile) {
      Swal.fire({
        icon: "warning",
        title: "Missing Image",
        text: "Please select an image.",
        confirmButtonColor: emerald600
      });
      return;
    }

    try {
      setLoading(true);
      
      // Create FormData
      const formDataObj = new FormData();
      
      // Append text fields
      formDataObj.append('title', plainTitle);
      formDataObj.append('subtitle', plainDescription);
      
      // CRITICAL: Append file if exists
      if (selectedFile) {
        formDataObj.append('image', selectedFile);
        console.log('File appended to FormData:', selectedFile.name);
      } else {
        console.log('No file to append');
      }

      // Log FormData contents
      console.log('FormData contents:');
      for (let pair of formDataObj.entries()) {
        if (pair[0] === 'image') {
          console.log('image: [File: ' + (pair[1]?.name || 'unknown') + ']');
        } else {
          console.log(pair[0] + ': ' + pair[1]);
        }
      }

      let response;
      if (activeTab === 'cards') {
        const url = isEditMode && currentIndex !== null 
          ? `/cards/${cards[currentIndex].id}/` 
          : '/cards/';
        const method = isEditMode ? 'put' : 'post';
        
        response = await api[method](url, formDataObj, {
          headers: { 
            'Content-Type': 'multipart/form-data',
          }
        });
        
        const cardsResponse = await api.get('/cards/');
        setCards(Array.isArray(cardsResponse.data) ? cardsResponse.data : []);

      } else if (activeTab === 'sliders') {
        const url = isEditMode && currentIndex !== null 
          ? `/sliders/${sliders[currentIndex].id}/` 
          : '/sliders/';
        const method = isEditMode ? 'put' : 'post';
        
        response = await api[method](url, formDataObj, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const slidersResponse = await api.get('/sliders/');
        setSliders(Array.isArray(slidersResponse.data) ? slidersResponse.data : []);

      } else if (activeTab === 'contents') {
        const data = {
          title: plainTitle,
          description: plainDescription
        };

        const url = isEditMode && contents.length > 0 
          ? `/contents/${contents[0].id}/` 
          : '/contents/';
        const method = isEditMode ? 'put' : 'post';
        
        response = await api[method](url, data);

        const contentsResponse = await api.get('/contents/');
        setContents(Array.isArray(contentsResponse.data) ? contentsResponse.data : []);
      }

      console.log('Success response:', response.data);

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: `Item ${isEditMode ? 'updated' : 'added'} successfully.`,
        showConfirmButton: false,
        timer: 1500
      });

      handleCloseModal();
    } catch (err) {
      console.error('Error:', err.response?.data || err.message);
      
      let errorMessage = 'Failed to save';
      if (err.response?.data) {
        if (err.response.data.image) {
          errorMessage = `Image error: ${err.response.data.image.join(', ')}`;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        }
      }

      Swal.fire({
        icon: "error",
        title: `Error ${err.response?.status || ''}`,
        text: errorMessage,
        confirmButtonColor: emerald600
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete function
  const handleDeleteItem = async (index) => {
    const confirmed = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Delete',
    });

    if (confirmed.isConfirmed) {
      try {
        let itemId;
        let endpoint = '';

        if (activeTab === 'cards') {
          if (!cards[index]) return;
          itemId = cards[index].id;
          endpoint = `/cards/${itemId}/`;
        } else if (activeTab === 'sliders') {
          if (!sliders[index]) return;
          itemId = sliders[index].id;
          endpoint = `/sliders/${itemId}/`;
        } else if (activeTab === 'contents') {
          if (!contents[index]) return;
          itemId = contents[index].id;
          endpoint = `/contents/${itemId}/`;
        }

        await api.delete(endpoint);

        if (activeTab === 'cards') {
          setCards(cards.filter((_, i) => i !== index));
        } else if (activeTab === 'sliders') {
          setSliders(sliders.filter((_, i) => i !== index));
        } else if (activeTab === 'contents') {
          setContents([]);
        }

        Swal.fire('Deleted!', 'Item has been deleted.', 'success');
      } catch (err) {
        Swal.fire('Error', 'Failed to delete', 'error');
      }
    }
  };

  // Edit function
  const handleEditItem = (index) => {
    let item;

    if (activeTab === 'cards') {
      if (!cards[index]) return;
      item = cards[index];
      setFormData({
        title: item.title || '',
        description: item.subtitle || '',
      });
    } else if (activeTab === 'sliders') {
      if (!sliders[index]) return;
      item = sliders[index];
      setFormData({
        title: item.title || '',
        description: item.subtitle || '',
      });
    } else if (activeTab === 'contents') {
      if (contents.length === 0 || !contents[0]) return;
      item = contents[0];
      setFormData({
        title: item.title || '',
        description: item.description || '',
      });
    }

    setCurrentIndex(index);

    // Set image preview for edit mode
    if (activeTab !== 'contents' && item) {
      if (item.image) {
        setImagePreview(item.image.startsWith('http') ? item.image : `https://backend.dlapakistan.com${item.image}`);
      } else if (item.image_url) {
        setImagePreview(item.image_url.startsWith('http') ? item.image_url : `https://backend.dlapakistan.com${item.image_url}`);
      }
    }

    setIsEditMode(true);
    setIsModalOpen(true);
  };

  // Handle edit for content
  const handleEditContent = () => {
    if (contents.length > 0 && contents[0]) {
      const content = contents[0];
      setFormData({
        title: content.title || '',
        description: content.description || '',
      });
      setCurrentIndex(0);
      setIsEditMode(true);
      setIsModalOpen(true);
    }
  };

  // View function
  const handleViewItem = (index) => {
    setCurrentIndex(index);
    setIsViewModalOpen(true);
  };

  // Close modal - RESET EVERYTHING
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setCurrentIndex(null);
    setImagePreview(null);
    setSelectedFile(null);
    setFormData({ title: '', description: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setCurrentIndex(null);
  };

  // Trigger file input
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Remove image
  const handleRemoveImage = () => {
    setImagePreview(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Helper function to get image URL
  const getImageUrl = (item) => {
    if (!item || activeTab === 'contents') return null;

    if (item.image_url) {
      return item.image_url.startsWith('http') ? item.image_url : `https://backend.dlapakistan.com${item.image_url}`;
    }
    if (item.image) {
      return item.image.startsWith('http') ? item.image : `https://backend.dlapakistan.com${item.image}`;
    }
    return 'https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';
  };

  // Empty states
  const renderEmptyState = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center">
        <Plus className="w-8 h-8 text-emerald-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">
        No {activeTab} yet
      </h3>
      <p className="text-gray-500 mb-6">
        Add your first {activeTab.slice(0, -1)}
      </p>
      <button
        onClick={() => {
          setIsEditMode(false);
          setImagePreview(null);
          setSelectedFile(null);
          setIsModalOpen(true);
        }}
        className="px-6 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium hover:from-emerald-600 hover:to-green-600 transition-all duration-300 shadow-md hover:shadow-lg"
      >
        Add {activeTab === 'cards' ? 'Card' : activeTab === 'sliders' ? 'Slider' : 'Content'}
      </button>
    </div>
  );

  const renderContentEmptyState = () => (
    <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
      <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
      <p className="text-gray-400 text-lg">No content added yet.</p>
      <button
        onClick={() => {
          setIsEditMode(false);
          setIsModalOpen(true);
        }}
        className="mt-4 px-6 py-2 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-all duration-300 shadow-md hover:shadow-lg"
      >
        Add Content
      </button>
    </div>
  );

  // Render grid
  const renderGrid = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      return renderEmptyState();
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-8">
        {items.map((item, index) => (
          <div key={item?.id || index} className="bg-white rounded-lg border border-gray-200 hover:border-emerald-300 transition-all duration-300 overflow-hidden shadow-sm hover:shadow-md group">
            <div className="relative h-40 overflow-hidden">
              <img
                src={getImageUrl(item)}
                alt={item?.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';
                }}
              />
              <div className="absolute top-2 right-2 flex space-x-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditItem(index);
                  }}
                  className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-emerald-50 shadow-sm transition-all duration-200 hover:scale-105"
                  title="Edit"
                >
                  <Edit className="w-4 h-4 text-emerald-600" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteItem(index);
                  }}
                  className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-red-50 shadow-sm transition-all duration-200 hover:scale-105"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>

            <div className="p-4 cursor-pointer" onClick={() => handleViewItem(index)}>
              <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-1">
                {item?.title}
              </h3>
              <p className="text-gray-600 text-sm line-clamp-2 mb-2">
                {item?.subtitle}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-emerald-600 font-medium">
                  {item?.created_at?.split('T')[0] || 'N/A'}
                </span>
                <Eye className="w-4 h-4 text-emerald-500" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render content
  const renderContent = () => {
    const contentArray = Array.isArray(contents) ? contents : [];
    
    if (contentArray.length === 0) {
      return renderContentEmptyState();
    }

    const content = contentArray[0];
    if (!content) {
      return renderContentEmptyState();
    }

    return (
      <div className="animate-fadeIn text-left w-full bg-transparent mt-8">
        <div className="mb-10 border-l-4 pl-6" style={{ borderColor: emerald600 }}>
          <div className="text-4xl font-bold text-gray-900 tracking-tight">
            {content.title || "No Title"}
          </div>
        </div>

        <div className="mt-12 border-t border-gray-200 pt-10 w-full bg-transparent">
          <div className="text-gray-600 text-lg leading-loose space-y-6 custom-description w-full prose prose-emerald max-w-none bg-transparent">
            {content.description}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1">
      {/* Header */}
      <div className="sticky top-14 z-10 bg-white px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-2">Manage your content</p>
          </div>

          {activeTab === 'contents' ? (
            <button
              onClick={Array.isArray(contents) && contents.length === 0 ? () => {
                setIsEditMode(false);
                setIsModalOpen(true);
              } : handleEditContent}
              style={{ backgroundColor: emerald600 }}
              className="flex items-center px-6 py-3 rounded-lg text-white font-medium hover:opacity-90 transition-all shadow-md hover:shadow-lg"
              disabled={loading}
            >
              {loading ? 'Loading...' : (
                Array.isArray(contents) && contents.length === 0 ? (
                  <><Plus className="w-5 h-5 mr-2" /> Add Content</>
                ) : (
                  <><Edit2 className="w-4 h-4 mr-2" /> Edit Content</>
                )
              )}
            </button>
          ) : (
            <button
              onClick={() => {
                setIsEditMode(false);
                setImagePreview(null);
                setSelectedFile(null);
                setIsModalOpen(true);
              }}
              className="flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-medium transition-all duration-300 shadow-md hover:shadow-lg"
              disabled={loading}
            >
              {loading ? 'Loading...' : (
                <>
                  <Plus className="w-5 h-5 mr-2" />
                  Add {activeTab === 'cards' ? 'Card' : 'Slider'}
                </>
              )}
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('cards')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'cards' 
                  ? 'border-emerald-500 text-emerald-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Cards
            </button>
            <button
              onClick={() => setActiveTab('sliders')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'sliders' 
                  ? 'border-emerald-500 text-emerald-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Sliders
            </button>
            <button
              onClick={() => setActiveTab('contents')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'contents' 
                  ? 'border-emerald-500 text-emerald-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Content
            </button>
          </nav>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading {activeTab}...</p>
          </div>
        ) : activeTab === 'cards' ? (
          renderGrid(cards)
        ) : activeTab === 'sliders' ? (
          renderGrid(sliders)
        ) : (
          renderContent()
        )}
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-emerald-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">
                {isEditMode ? 'Edit' : 'Add'} {activeTab === 'cards' ? 'Card' : activeTab === 'sliders' ? 'Slider' : 'Content'}
              </h2>
              <button onClick={handleCloseModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                {/* Image upload for cards and sliders */}
                {activeTab !== 'contents' && (
                  <>
                    <input 
                      ref={fileInputRef} 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileSelect}
                      className="hidden" 
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Image {activeTab === 'cards' ? '(Optional)' : '(Required)'}
                      </label>
                      {imagePreview ? (
                        <div className="border-2 border-dashed border-emerald-200 rounded-xl p-4">
                          <img src={imagePreview} alt="Preview" className="w-full h-48 object-contain rounded-lg mb-4" />
                          <div className="flex justify-center space-x-3">
                            <button type="button" onClick={triggerFileInput} className="flex items-center px-4 py-2 bg-emerald-500 text-white rounded-lg">
                              <Replace className="w-4 h-4 mr-2" />
                              Replace
                            </button>
                            <button type="button" onClick={handleRemoveImage} className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Remove
                            </button>
                          </div>
                          {selectedFile && (
                            <p className="text-xs text-emerald-600 mt-2 text-center">
                              Selected: {selectedFile.name}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div 
                          onClick={triggerFileInput} 
                          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-400 transition-colors"
                        >
                          <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                          <p className="text-gray-600">Click to upload image</p>
                          <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 5MB</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Title field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <div className="bg-white rounded-xl border border-gray-300 overflow-hidden" style={{ minHeight: '150px' }}>
                    <JoditEditor
                      ref={titleEditorRef}
                      value={formData.title}
                      config={editorConfig}
                      onBlur={(newContent) => {
                        setFormData(prev => ({ ...prev, title: newContent }));
                      }}
                    />
                  </div>
                </div>

                {/* Description field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {activeTab === 'cards' ? 'Description' : activeTab === 'sliders' ? 'Subtitle' : 'Description'} *
                  </label>
                  {activeTab === 'cards' || activeTab === 'contents' ? (
                    <div className="bg-white rounded-xl border border-gray-300 overflow-hidden" style={{ minHeight: '250px' }}>
                      <JoditEditor
                        ref={descriptionEditorRef}
                        value={formData.description}
                        config={editorConfig}
                        onBlur={(newContent) => {
                          setFormData(prev => ({ ...prev, description: newContent }));
                        }}
                      />
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={stripHtml(formData.description)}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      required
                    />
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button type="button" onClick={handleCloseModal} className="px-5 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600" disabled={loading}>
                  {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {isViewModalOpen && currentIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-emerald-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Details</h2>
              <button onClick={handleCloseViewModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {(() => {
                let item;
                if (activeTab === 'cards') {
                  item = cards[currentIndex];
                } else if (activeTab === 'sliders') {
                  item = sliders[currentIndex];
                } else {
                  const contentArray = Array.isArray(contents) ? contents : [];
                  item = contentArray[currentIndex] || contentArray[0];
                }

                if (!item) {
                  return <p className="text-gray-500">No item found</p>;
                }

                return (
                  <>
                    {activeTab !== 'contents' && (
                      <img
                        src={getImageUrl(item)}
                        alt={item.title}
                        className="w-full h-64 object-cover rounded-lg mb-4"
                      />
                    )}

                    <div className="text-xl font-bold mb-2">
                      {item.title}
                    </div>

                    {activeTab === 'contents' ? (
                      <div className="mt-4">
                        <div className="prose prose-emerald max-w-none">
                          {item.description}
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-700 mb-4 whitespace-pre-wrap">
                        {item.subtitle}
                      </p>
                    )}

                    <p className="text-sm text-gray-500 mt-4">
                      Created: {item.created_at?.split('T')[0] || 'N/A'}
                    </p>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;